/*!
 * Copyright (c) Trimble Inc. All rights reserved
 */

import { Component, OnInit, Output, EventEmitter, Input } from '@angular/core';
import { SearchContentService, SearchService } from 'api/services';
import { GenericTableData } from 'src/app/sharedServices/generic-table-data.service';
import { LoaderService } from 'src/app/sharedServices/loader.service';
import { FormControl } from '@angular/forms';
import {
  ContentItemDto,
  ManufacturerDto,
  ManufacturerProductLineDto,
  FittingUnitDto,
} from 'api/models';
import { Observable } from 'rxjs';
import { startWith, map } from 'rxjs/operators';
import { SignalRService } from 'src/app/sharedServices/signal-r.service';
import { FittingState } from 'src/app/models/enums/FittingState.enum';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { BuildableContentProductsDto } from 'api/models/buildable-content-products-dto';
import { PaginationService } from 'src/app/sharedServices/pagination.service';

@Component({
  selector: 'app-search-content',
  templateUrl: './search-content.component.html',
  styleUrls: ['./search-content.component.css'],
})
export class SearchContentComponent implements OnInit {
  @Input() title: string;
  @Input() disabledUnits: boolean = false;
  @Input() disabledProductLines: boolean = false;
  @Input() disabledFittingState: boolean = false;
  @Input() columnHeader: any;
  @Input() isBuildableContent = false;

  contentItems: ContentItemDto[];
  buildableContentProducts: BuildableContentProductsDto[];
  searchText: string;
  defaultSelectedUnit = true;
  unitsType = 1;
  unitsList: FittingUnitDto[];
  filteredUnits: FittingUnitDto[];
  searchFormControl = new FormControl({ Disabled: true });
  searchInput = false;
  manufacturerList: ManufacturerDto[];
  filteredManufacturers: Observable<ManufacturerDto[]>;
  manufNameFormControl = new FormControl({ Disabled: false });
  selectedManufacturer: ManufacturerDto;
  productLineList: ManufacturerProductLineDto[];
  productLineFormControl = new FormControl({ Disabled: true });
  filteredProductLines: Observable<ManufacturerProductLineDto[]>;
  selectedProductLine: ManufacturerProductLineDto;
  selectedProductLineId: number;
  fittingRadioButtonStateState: FittingState;
  fittingStateOptions = [];
  fittingState: boolean;
  GetBuildableContentItemsPath = '/api/searchContent/BuildableContentProducts';

  constructor(
    private readonly searchContentItems: SearchContentService,
    private readonly fittingDataService: GenericTableData,
    private readonly paginationService: PaginationService,
    private readonly loaderService: LoaderService,
    private readonly searchService: SearchService,
    private readonly signalRService: SignalRService
  ) {
    this.setColumnHeaders();
    this.paginationService.setEnpointUrl(this.GetBuildableContentItemsPath);
    this.signalRService.buildConnection();
  }

  setColumnHeaders() {
    this.fittingDataService.setTableHeader(this.columnHeader);
    return this.columnHeader;
  }

  ngOnInit() {
    this.refresh();
    this.getUnits();
    this.searchFormControl.disable();
    this.manufNameFormControl.enable();
    this.fittingRadioButtonStateState = FittingState.Continue;
    this.fittingState = false;
    this.fittingStateOptions = Object.keys(FittingState);
  }

  clearInputFilds() {
    this.searchText = null;
    this.selectedManufacturer = null;
    this.contentItems = null;
    this.paginationService.setKeywordAndManufacturer(null, null);
    this.refresh();
  }

  refresh() {
    this.setColumnHeaders();
    this.manufNameFormControl.reset();
    this.manufNameFormControl.disable();
    this.productLineFormControl.reset();
    this.productLineFormControl.disable();
    this.paginationService.setKeywordAndManufacturer(null, null);
    this.resetDataTable();
    this.getAllManufacturers();
  }

  async getUnits() {
    this.searchService.getFittingUnits().subscribe((unitList) => {
      this.unitsList = unitList;
      this.unitsType = this.unitsList[0].unitsOriginID;
    });
  }

  async getAllManufacturers() {
    this.searchService
      .getManufacturers({ unitId: this.unitsType })
      .subscribe((manufacturers) => {
        this.manufacturerList = manufacturers;
        this.filteredManufacturers =
          this.manufNameFormControl.valueChanges.pipe(
            startWith(''),
            map((value) => this.filterManufacturers(value))
          );
      });
  }

  async getProductLines() {
    this.searchService
      .getProductLines({
        unitId: this.unitsType,
        manufacturerId: this.selectedManufacturer.manufID,
      })
      .subscribe((productLines) => {
        this.productLineList = productLines;
        this.filteredProductLines =
          this.productLineFormControl.valueChanges.pipe(
            startWith(''),
            map((value) => this.filterProductLines(value))
          );
      });
  }

  async search() {
    if (this.isBuildableContent) {
      this.searchBuildableContent();
    } else {
      this.searchContent();
    }
  }

  async searchContent() {
    this.loaderService.display(true);
    const id = this.selectedManufacturer
      ? this.selectedManufacturer.manufID
      : null;
    // tslint:disable-next-line: max-line-length
    this.searchContentItems
      .getContentItems({
        searchWords: this.searchText,
        manufacturerId: id,
        manufProductLineId: this.selectedProductLineId,
        unitId: this.unitsType,
        discontinued: this.fittingState,
      })
      .subscribe((records) => {
        this.contentItems = records;
        this.fittingDataService.setTableDataList(records);
        // searching by selected manufacturer
        if (id !== null) {
          this.getProductLines();
        }
        this.loaderService.display(false);
      });
  }

  async searchBuildableContent() {
    this.loaderService.display(true);
    const manufName = this.selectedManufacturer
      ? this.selectedManufacturer.manufName
      : null;
    // tslint:disable-next-line: max-line-length
    this.searchContentItems
      .getBuildableContentItems({
        filterText: this.searchText,
        manufacturerName: manufName,
        pageNumber: 1,
        pageSize: this.fittingDataService.getPageSize(),
      })
      .subscribe((records) => {
        if (records !== null && records.buildableContentProducts !== null) {
          this.buildableContentProducts = records.buildableContentProducts;
          this.fittingDataService.setTotalItems(records.totalCount);
          this.fittingDataService.setTableDataList(
            records.buildableContentProducts
          );
          this.paginationService.setQueryParams({
            manufacturerName: manufName,
            filterText: this.searchText,
          });
          this.paginationService.setKeywordAndManufacturer(
            this.searchText,
            manufName
          );
         
        }
        this.loaderService.display(false);
      });
  }

  filterContent() {
    this.fittingDataService.setTableDataList(this.productLineList);
  }

  filterManufacturers(value): ManufacturerDto[] {
    const filterValue =
      value === null
        ? ''
        : typeof value === 'object'
        ? value.manufName.toLocaleLowerCase()
        : value.toLocaleLowerCase();
    return this.manufacturerList.filter((manufacturer) =>
      manufacturer.manufName.toLocaleLowerCase().includes(filterValue)
    );
  }

  filterProductLines(value): ManufacturerProductLineDto[] {
    const filterValue =
      value === null
        ? ''
        : typeof value === 'object'
        ? value.productLine.toLocaleLowerCase()
        : value.toLocaleLowerCase();
    return this.productLineList.filter((content) =>
      content.productLine.toLocaleLowerCase().includes(filterValue)
    );
  }

  clearManufacturer() {
    this.selectedManufacturer = null;
    this.paginationService.setKeywordAndManufacturer(null, null);
    this.manufNameFormControl.reset();
    this.resetProductLineForm();
    this.resetDataTable();
  }

  clearProductLine() {
    this.selectedProductLine = null;
    this.selectedProductLineId = null;
    this.productLineFormControl.reset();
  }

  displayManufName(manuf: ManufacturerDto) {
    return manuf ? manuf.manufName : '';
  }

  displayProductLineName(productLine: ContentItemDto) {
    return productLine ? productLine.productLine : '';
  }

  selectManufacturer($event: MatAutocompleteSelectedEvent) {
    this.selectedManufacturer = $event.option.value;
    this.selectedProductLine = null;
    this.selectedProductLineId = null;
    this.resetDataTable();
    this.getProductLines();
    this.productLineFormControl.disabled && this.disabledProductLines !== true
      ? this.productLineFormControl.enable()
      : this.productLineFormControl.reset();
  }

  selectProductLine($event: MatAutocompleteSelectedEvent) {
    this.selectedProductLine = $event.option.value;
    this.selectedProductLineId = this.selectedProductLine?.manufProductLineID;
    if (this.contentItems) {
      if (this.selectedProductLine) {
        this.loaderService.display(true);
        this.resetDataTable();
        const filteredProductLines = this.contentItems.filter((content) =>
          content.productLine
            .toLowerCase()
            .includes(this.selectedProductLine.productLine.toLowerCase())
        );
        this.fittingDataService.setTableDataList(filteredProductLines);
        this.loaderService.display(false);
      } else {
        this.productLineFormControl.disable();
        this.resetDataTable();
      }
    }
  }

  resetDataTable() {
    this.fittingDataService.setTableDataList(null);
  }

  isSearchingDisabled() {
    return (this.searchText === undefined ||
      this.searchText === null ||
      this.searchText === '') &&
      (this.selectedManufacturer === undefined ||
        this.selectedManufacturer === null)
      ? true
      : false;
  }

  checkSelectedUnit() {
    return this.defaultSelectedUnit === true ? false : true;
  }

  checkSelectedSearchType() {
    return this.searchInput === true ? false : true;
  }

  setContentUnit(event) {
    this.defaultSelectedUnit = event.checked ? false : true;
    this.defaultSelectedUnit === true
      ? (this.unitsType = this.unitsList[0].unitsOriginID)
      : (this.unitsType = this.unitsList[1].unitsOriginID);
    this.getAllManufacturers();
  }

  setSearchType(event) {
    this.searchInput = event.checked ? false : true;
    this.resetFormControls();
  }

  checkFittingState() {
    return this.fittingState;
  }

  setFittingState(event) {
    this.fittingState = event.checked;
    this.resetDataTable();
  }

  resetFormControls() {
    this.searchText = null;
    this.selectedManufacturer = null;
    this.contentItems = null;
    this.searchFormControl.reset();
    this.manufNameFormControl.reset();
    this.productLineFormControl.reset();
    this.productLineFormControl.disable();
    this.resetDataTable();
    this.searchInput === true
      ? this.searchFormControl.enable()
      : this.searchFormControl.disable();
    this.searchInput === true
      ? this.manufNameFormControl.disable()
      : this.manufNameFormControl.enable();
    this.getAllManufacturers();
  }

  resetProductLineForm() {
    this.productLineFormControl.reset();
    this.productLineFormControl.disable();
    this.resetDataTable();
  }
}
